import { AHText, Box } from "arkh";
import { View } from "react-native";
import { graphql, useFragment } from "react-relay";

import { ListItemAssignees_employees$key } from "./__generated__/ListItemAssignees_employees.graphql";
import { ListItemAssignees_roles$key } from "./__generated__/ListItemAssignees_roles.graphql";
import { ListItemEmployees } from "./ListItemEmployees";
import { ListItemRoles } from "./ListItemRoles";

export type ListItemAssigneesProps = {
  employees: ListItemAssignees_employees$key;
  roles: ListItemAssignees_roles$key;
  currentEmployeeId: string | undefined;
  maxWidth?: number;
  textType?: "body" | "detail";
};

export function ListItemAssignees(props: ListItemAssigneesProps) {
  const { currentEmployeeId, maxWidth } = props;

  const employees = useFragment(
    graphql`
      fragment ListItemAssignees_employees on Employee @relay(plural: true) {
        id
        ...ListItemEmployees_employees
      }
    `,
    props.employees
  );

  const roles = useFragment(
    graphql`
      fragment ListItemAssignees_roles on EmployeeRole @relay(plural: true) {
        id
        ...ListItemRoles_roles
      }
    `,
    props.roles
  );

  if (employees.length === 0 && roles.length === 0) {
    return null;
  }

  const maxEmployees = Math.min(employees.length, roles.length > 0 ? 3 : 5);
  const maxRoles = Math.min(roles.length, employees.length > 0 ? 1 : 2);

  const remainingEmployees = employees.length - maxEmployees;
  const remainingRoles = roles.length - maxRoles;
  const totalRemaining = remainingEmployees + remainingRoles;

  const hasEmployees = employees.length > 0;
  const hasRoles = roles.length > 0;

  return (
    <View
      style={{ flexDirection: "row", alignItems: "center", overflow: "hidden" }}
    >
      {hasEmployees ? (
        <ListItemEmployees
          employees={employees}
          effectiveMaxEmployees={maxEmployees}
          highlightViewer={true}
          currentEmployeeId={currentEmployeeId}
          maxWidth={maxWidth}
          nameDisplayMode="last-only"
          textType={props.textType}
          spacing="xsmall"
        />
      ) : null}
      {hasRoles ? (
        <Box spaceLeft={hasEmployees ? "xsmall" : undefined}>
          <ListItemRoles
            roles={roles}
            currentEmployeeId={currentEmployeeId}
            highlightViewerRoles={true}
            maxShowingRoles={maxRoles}
            textType={props.textType ?? "body"}
            spacing="xsmall"
          />
        </Box>
      ) : null}
      {totalRemaining > 0 ? (
        <Box spaceLeft="xsmall">
          <AHText type="info-small" color="text-washed">
            {`+${totalRemaining}`}
          </AHText>
        </Box>
      ) : null}
    </View>
  );
}
